import { saveAnswer } from '@/utils/onboardingStorage'
import PageLayout from '@app-studio-web/ui/components/PageLayout'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

function getRiskLevel(s: number) {
  if (s < 20) return { label: 'Minimal', color: '#3b82f6' }
  if (s < 40) return { label: 'Low', color: '#22c55e' }
  if (s < 60) return { label: 'Moderate', color: '#e8934a' }
  if (s < 80) return { label: 'High', color: '#f97316' }
  return { label: 'Critical', color: '#ef4444' }
}

export default function ScorePreparingPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [score, setScore] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const targetRef = useRef(42 + Math.floor(Math.random() * 40))

  useEffect(() => {
    const target = targetRef.current
    let current = 0
    let cancelled = false
    let slowTimer: ReturnType<typeof setTimeout>
    let navTimer: ReturnType<typeof setTimeout>

    const finalize = () => {
      setIsComplete(true)
      saveAnswer('score', {
        question_key: 'score',
        question: 'AI Exposure Score',
        answer_key: String(target),
        answer: String(target),
      })
      navTimer = setTimeout(() => {
        if (!cancelled) navigate('/checkout/plans', { replace: true })
      }, 2200)
    }

    const slowThreshold = target - 8

    const fastTimer = setInterval(() => {
      current++
      if (current >= slowThreshold) {
        clearInterval(fastTimer)
        const slowCount = (c: number) => {
          if (cancelled) return
          if (c > target) {
            finalize()
            return
          }
          setScore(c)
          slowTimer = setTimeout(() => slowCount(c + 1), 150)
        }
        slowCount(current)
        return
      }
      setScore(current)
    }, 30)

    return () => {
      cancelled = true
      clearInterval(fastTimer)
      clearTimeout(slowTimer)
      clearTimeout(navTimer)
    }
  }, [navigate])

  const risk = getRiskLevel(score)
  const circumference = 2 * Math.PI * 84
  const progress = score / 100

  return (
    <PageLayout>
      <div className="flex grow flex-col items-center justify-center gap-6 text-center">
        <div className="relative mx-auto h-[200px] w-[200px]">
          <svg
            className="-rotate-90"
            viewBox="0 0 180 180"
            width="200"
            height="200"
          >
            <circle
              cx="90"
              cy="90"
              r="84"
              fill="none"
              stroke="#e4dfd6"
              strokeWidth="5"
            />
            <circle
              cx="90"
              cy="90"
              r="84"
              fill="none"
              stroke={risk.color}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - progress)}
              className="transition-[stroke] duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-primary -translate-y-0.5 font-serif text-[3.5rem] leading-none font-normal tabular-nums">
              {score}
            </span>
          </div>
        </div>

        <p
          className="text-[0.75rem] font-semibold tracking-[2px] uppercase transition-colors duration-500"
          style={{ color: risk.color }}
        >
          {t('pages.scorePreparing.label', {
            defaultValue: 'AI Exposure Score',
          })}
        </p>

        <h2 className="text-primary font-serif text-3xl font-normal">
          {isComplete
            ? t('pages.scorePreparing.complete', {
                defaultValue: '{{level}} Exposure',
                level: risk.label,
              })
            : t('pages.scorePreparing.calculating', {
                defaultValue: 'Calculating...',
              })}
        </h2>

        {!isComplete && (
          <div className="my-5 flex justify-center gap-1.5">
            {[0, 0.2, 0.4].map((delay) => (
              <span
                key={delay}
                className="bg-primary h-2.5 w-2.5 rounded-full"
                style={{
                  animation: `typingBounce 1.4s ease-in-out ${delay}s infinite`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-12px); opacity: 1; }
        }
      `}</style>
    </PageLayout>
  )
}
